import { useEffect, useRef, useState } from "react";
import { Fade, Box, Typography, useTheme } from "@mui/material";
import { merge } from "lodash";
import { makeSxStyles } from "ui";
import { showCustomerStatus } from "../../route-planner/helpers";
import { MapMarker } from "../../../../components/map/map.types";
import {
  paletteColorStringRenderer,
  formatNumberToMinutes,
  formatNumberToMinutesPrecise,
  formattedValueWithSymbol,
} from "../../../../shared/utils/string-helpers";
import { RouteStatus } from "../../route-planner/helpers/route-status.enum";
import { DateTime } from "luxon";
import { MeasurementSystemEnum } from "service";
import useGlobalStore from "../../../../state/global-store";

type HoverAddressProps = {
  isHovering: boolean;
  hoveredAddress: MapMarker | null;
  handleBoxMouseEnter: () => void;
  handleBoxMouseLeave: () => void;
  hoverPosition: { x: number; y: number };
  fadeRef: React.RefObject<HTMLDivElement>;
  adjustedServiceTimeMap?: Record<string, number>; // Map of locationId -> adjusted service time
  adjustedEstimatedTimeMinutesMap?: Record<string, number>; // Map of locationId -> adjusted estimated time minutes (from real-time delays)
  estimatedArrivalTimeMap?: Record<string, number>; // Map of locationId -> calculated ETA based on array position (for reordered addresses)
  serviceTimeMap?: Record<string, number>; // Map of locationId -> calculated service time (serviceTimeMin - previous serviceTimeMin)
  currentRouteStatus?: RouteStatus; // Current route status for precise formatting in live phase
};

const HoveredAddress = ({
  isHovering,
  hoveredAddress,
  handleBoxMouseEnter,
  handleBoxMouseLeave,
  hoverPosition,
  adjustedServiceTimeMap,
  adjustedEstimatedTimeMinutesMap,
  estimatedArrivalTimeMap,
  serviceTimeMap,
  currentRouteStatus,
}: HoverAddressProps) => {
  const styles = useSxStyles();
  const theme = useTheme();
  const { palette } = theme;
  const { system } = useGlobalStore();
  const [boxWidth, setBoxWidth] = useState(240);
  const [regionWidth, setRegionWidth] = useState(100);
  const boxRef = useRef<HTMLDivElement>(null);
  const regionRef = useRef<HTMLDivElement>(null);

  // Use precise formatting in live phase
  const isLivePhase = currentRouteStatus === RouteStatus.Live;
  const formatTime = isLivePhase
    ? formatNumberToMinutesPrecise
    : formatNumberToMinutes;

  // Helper function to calculate estimated arrival time
  // In live phase, show seconds for precision; otherwise show h:mm a format
  const calculateEstimatedArrival = (
    routeStartTime?: string,
    estimatedTimeMinutes?: number,
    locationSequence?: number | null,
  ): string | null => {
    if (!routeStartTime) {
      return null;
    }

    // Time format: show seconds in live phase for precision
    const timeFormat = isLivePhase ? "h:mm:ss a" : "h:mm a";

    // Special case: If this is the first address and estimatedTimeMinutes is 0 or not present,
    // show the route start time as the estimated arrival
    if (
      locationSequence === 1 &&
      (!estimatedTimeMinutes || estimatedTimeMinutes <= 0)
    ) {
      try {
        const [hours, minutes] = routeStartTime.split(":").map(Number);
        const startTime = DateTime.now().set({
          hour: hours,
          minute: minutes,
          second: 0,
        });
        return startTime.toFormat(timeFormat);
      } catch {
        return null;
      }
    }

    // For all other cases: return null if estimatedTimeMinutes is not valid
    if (!estimatedTimeMinutes || estimatedTimeMinutes <= 0) {
      return null;
    }

    try {
      // Parse the route start time (format: "HH:mm")
      const [hours, minutes] = routeStartTime.split(":").map(Number);
      const startTime = DateTime.now().set({
        hour: hours,
        minute: minutes,
        second: 0,
      });

      // Add estimated time - convert minutes to seconds for precision
      // e.g., 45.5 minutes = 45 minutes and 30 seconds
      const totalSeconds = Math.round(estimatedTimeMinutes * 60);
      const arrivalTime = startTime.plus({ seconds: totalSeconds });

      // Format based on phase
      return arrivalTime.toFormat(timeFormat);
    } catch {
      return null;
    }
  };

  // Use real-time adjusted ETA first (when delays are detected), then fall back to
  // array-position ETA (for reordered addresses), then to backend's original value.
  // Priority: 1. adjustedEstimatedTimeMinutesMap (real-time delay adjustments)
  //           2. estimatedArrivalTimeMap (correct for reordered addresses)
  //           3. hoveredAddress.travelDetails.estimatedTimeMinutes (backend's original value)
  const effectiveEstimatedTimeMinutes =
    adjustedEstimatedTimeMinutesMap?.[hoveredAddress?.id ?? ""] ??
    estimatedArrivalTimeMap?.[hoveredAddress?.id ?? ""] ??
    hoveredAddress?.travelDetails?.estimatedTimeMinutes;

  const estimatedArrival = calculateEstimatedArrival(
    hoveredAddress?.routeStartTime,
    effectiveEstimatedTimeMinutes,
    hoveredAddress?.locationSequence,
  );

  // EST Snow — formatted in the user's selected measurement system.
  const estSnow = hoveredAddress?.snowAmount
    ? formattedValueWithSymbol(
        system === MeasurementSystemEnum.metric
          ? hoveredAddress.snowAmount.metric
          : hoveredAddress.snowAmount.imperial,
        system,
      )
    : null;

  useEffect(() => {
    let timeout: NodeJS.Timeout;

    if (isHovering && hoveredAddress) {
      // Only recalculate dimensions when tooltip becomes visible
      timeout = setTimeout(() => {
        if (boxRef.current) {
          setBoxWidth(boxRef.current.offsetWidth);
        }
        if (regionRef.current) {
          setRegionWidth(regionRef.current.offsetWidth);
        }
      }, 0); // Use next tick to ensure DOM is updated
    }
    return () => clearTimeout(timeout);
  }, [isHovering, hoveredAddress]);

  return (
    <Fade in={isHovering && hoveredAddress !== null} timeout={150}>
      <Box
        ref={boxRef}
        sx={merge(styles.hoverBox, {
          left: hoverPosition.x - boxWidth / 2,
          bottom: window.innerHeight - hoverPosition.y + 20,
        })}
        onMouseEnter={handleBoxMouseEnter}
        onMouseLeave={handleBoxMouseLeave}
      >
        {hoveredAddress && (
          <Box data-testid="hovered-address-tooltip">
            <Typography
              data-testid="hovered-address-tooltip-address"
              sx={{
                fontSize: "14px",
                lineHeight: "20px",
                fontWeight: 600,
                color: "rgba(138, 206, 255, 1)",
                mb: "4px",
                maxWidth: regionWidth <= 240 ? "240px" : "none",
              }}
            >
              {hoveredAddress.address}
            </Typography>
            <Typography
              data-testid="hovered-address-tooltip-customer"
              sx={styles.customerName}
            >
              {hoveredAddress.customer?.firstName}{" "}
              {hoveredAddress.customer?.lastName}
            </Typography>
            <Box sx={styles.detailsWrapper}>
              <Typography sx={styles.labelText}>Region:</Typography>
              <Box
                ref={regionRef}
                sx={styles.valueText}
                data-testid="hovered-address-tooltip-region-name"
              >
                <Box
                  sx={{
                    display: "inline-flex",
                    width: "6px",
                    height: "13px",
                    borderRadius: "2px",
                    marginRight: "6px",
                    backgroundColor:
                      paletteColorStringRenderer(
                        palette,
                        (
                          hoveredAddress.region?.color ?? hoveredAddress.color
                        )?.toLowerCase(),
                        hoveredAddress.selected,
                        hoveredAddress.status,
                      ) ??
                      hoveredAddress.region?.color ??
                      hoveredAddress.color,
                    alignSelf: "center",
                  }}
                />
                {hoveredAddress.region?.name ?? hoveredAddress.routeName}
              </Box>
              {!!hoveredAddress.locationSequence &&
                hoveredAddress.status !== "skipped" &&
                hoveredAddress.routeId !== "unassigned-locations" && (
                  <>
                    <Typography sx={styles.labelText}>Sequence:</Typography>
                    <Typography
                      data-testid="hovered-address-tooltip-sequence"
                      sx={styles.valueText}
                    >
                      {hoveredAddress.locationSequence}
                    </Typography>
                  </>
                )}
              {estimatedArrival && (
                <>
                  <Typography sx={styles.labelText}>EST Arrival:</Typography>
                  <Typography
                    data-testid="hovered-address-tooltip-estimated-arrival"
                    sx={styles.valueText}
                  >
                    {estimatedArrival}
                  </Typography>
                </>
              )}
              <>
                <Typography sx={styles.labelText}>Service Duration:</Typography>
                <Typography
                  data-testid="hovered-address-tooltip-duration"
                  sx={styles.valueText}
                >
                  {formatTime(
                    Number(
                      adjustedServiceTimeMap?.[hoveredAddress.id] ??
                        serviceTimeMap?.[hoveredAddress.id] ??
                        hoveredAddress.duration,
                    ),
                  )}
                </Typography>
              </>
              {estSnow && (
                <>
                  <Typography sx={styles.labelText}>EST Snow:</Typography>
                  <Typography
                    data-testid="hovered-address-tooltip-est-snow"
                    sx={styles.valueText}
                  >
                    {estSnow}
                  </Typography>
                </>
              )}
              {hoveredAddress.status && (
                <>
                  <Typography sx={styles.labelText}>Status:</Typography>
                  <Typography sx={styles.valueText}>
                    {showCustomerStatus(hoveredAddress.status)}
                  </Typography>
                </>
              )}
            </Box>
          </Box>
        )}
      </Box>
    </Fade>
  );
};

const useSxStyles = makeSxStyles(() => ({
  hoverBox: {
    position: "fixed",
    backgroundColor: "#282A2D",
    boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.06)",
    padding: "16px",
    borderRadius: "8px",
    zIndex: 1001,
    minWidth: "240px",
    maxWidth: "400px",
    minHeight: "160px",

    "&::before": {
      content: '""',
      position: "absolute",
      width: 0,
      height: 0,
      borderLeft: "8px solid transparent",
      borderRight: "8px solid transparent",
      borderTop: "8px solid #282A2D",
      bottom: "-8px",
      left: "50%",
      transform: "translateX(-50%)",
    },
  },
  customerName: {
    fontSize: "14px",
    lineHeight: "20px",
    fontWeight: 500,
    color: "#C1C7CE",
    mb: "16px",
  },
  detailsWrapper: {
    display: "grid",
    width: "100%",
    gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
    columnGap: "8px",
    rowGap: "4px",
    alignItems: "center",
  },
  labelText: {
    fontSize: "12px",
    lineHeight: "16px",
    fontWeight: 500,
    color: "#C1C7CE",
    width: "100%",
    display: "inline-flex",
    alignItems: "center",
  },
  valueText: {
    fontSize: "14px",
    lineHeight: "20px",
    fontWeight: 400,
    letterSpacing: "0.1px",
    color: "#C5C6C9",
    width: "100%",
    display: "inline-flex",
    alignItems: "center",
  },
}));

export default HoveredAddress;
